import React, { useEffect, useState } from 'react';
import { ThreePanesWithShrinkableSidebar } from 'components/views/molecules/ThreePanesWithShrinkableSidebar';
import { RemoteMonitor } from './RemoteMonitor';
import { withMultiScreenClientBinding, useRemoteVehicleId, useSubScreenLayouts, useNumberOfScreens } from './withMultiScreenClientBinding';
import { SideNavigationBar } from 'components/views/organisms/SideNavigationBar';
import { useSelectedProject } from 'services/useSelectedProject';
import { createGlobalState } from 'services/createGlobalState';
import { useEnvironments } from 'services/useEnvironments';
import { useSelectedEnvironment } from 'services/useSelectedEnvironment';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { SubScreenLayout } from 'core/models/layout';

const {
    useWithState: useWithLocalSelection,
    useState: useLocalSelection,
    useDispatch: useSetLocalSelection,
} = createGlobalState<number | null>((_, v) => v, null);

function Header() {
    const numberOfScreens = useNumberOfScreens();

    const localSelection = useLocalSelection();
    const setLocalSelection = useSetLocalSelection();

    useEffect(() => {
        if (localSelection != null) return;
        setLocalSelection(numberOfScreens);
    }, [localSelection, numberOfScreens, setLocalSelection]);

    return (
        <SideNavigationBar
            numberOfScreens={numberOfScreens}
            selected={localSelection}
            onSelect={setLocalSelection}
        />
    );
}

function Body() {
    const projectId = useSelectedProject()?.id;
    const vehicleId = useRemoteVehicleId();
    const environments = useEnvironments(projectId);
    const environmentId = useSelectedEnvironment(environments)?.id;
    const layouts = useSubScreenLayouts();
    const localSelection = useLocalSelection();

    const [layout, setLayout] = useState<SubScreenLayout>();
    useEffect(() => {
        if (!layouts) {
            setLayout(undefined);
            return;
        }

        const idx = localSelection != null
            ? localSelection - 1
            : 0
            ;

        const selected = (idx < layouts.length)
            ? layouts[idx]
            : undefined
            ;

        setLayout(selected);
    }, [layouts, localSelection]);

    return (
        <ErrorBoundary fallbackRender={(props: FallbackProps) => {
            const { error } = props;
            return <div>
                <div>
                    <span>Error on RemoteMonitor</span>
                    <div>Error:{error.message}</div>
                    <div>Stacktrace:{error.stack}</div>
                </div>
            </div>;
        }}>
            <RemoteMonitor projectId={projectId} vehicleId={vehicleId} environmentId={environmentId} layout={layout} />
        </ErrorBoundary>
    );
}

function Wrap() {
    const withLocalSelection = useWithLocalSelection();

    const [navbarShouldVisible, setNavbarShouldVisible] = useState(true);
    function onMouseMove(event: React.MouseEvent<HTMLDivElement>) {
        setNavbarShouldVisible(event.clientY < 30);
    }

    const [navbarIsInUse, setNavbarIsInUse] = useState(false);

    return withLocalSelection(
        <ErrorBoundary fallbackRender={(props) => {
            const { error } = props;
            return <div>
                <div>
                    <span>Error on SidePage</span>
                    <div>Error:{error.message}</div>
                    <div>Stacktrace:{error.stack}</div>
                </div>
            </div>;
        }}>
            <div onMouseMove={onMouseMove}>
                <ThreePanesWithShrinkableSidebar sidebarVisible={false} navbarVisible={navbarIsInUse || navbarShouldVisible}>
                    <div onMouseEnter={() => setNavbarIsInUse(true)} onMouseLeave={() => setNavbarIsInUse(false)}>
                        <Header />
                    </div>
                    <React.Fragment />
                    <Body />
                </ThreePanesWithShrinkableSidebar>
            </div>
        </ErrorBoundary>
    );
}

export function SidePage() {
    return withMultiScreenClientBinding(<Wrap />);
}
